import type { CSSProperties } from 'react';
import type { IToggleSwitch } from './ToggleSwitch.interface';
import styles from './ToggleSwitch.module.css';

const DEFAULT_TOGGLE_ON = '#FFFFFF';
const DEFAULT_TOGGLE_OFF = '#FFFFFF';
const DEFAULT_BACKGROUND_ON = '#2CD057';
const DEFAULT_BACKGROUND_OFF = '#D3D2D6';

export enum StyleElement {
    Container = 'Container',
    Checkbox = 'Checkbox',
    Label = 'Label',
}

const DEFAULTS: Record<StyleElement, CSSProperties> = {
    [StyleElement.Container]: {},
    [StyleElement.Checkbox]: {},
    [StyleElement.Label]: {},
};

export function classNameForElement(
    element: StyleElement,
    model: IToggleSwitch,
    overrides?: string,
): string {
    const name = element.toString();
    const classLoad = styles[name];
    if (classLoad === undefined) throw new Error(`Couldn't find style class for element: ${name}`);
    let returnClassName = classLoad;

    if ((overrides?.length ?? 0) > 0) {
        returnClassName += ` ${overrides ?? ''}`;
    }

    return returnClassName;
}

const PROPS_APPLICATOR: Record<StyleElement, (properties: IToggleSwitch) => CSSProperties> = {
    [StyleElement.Container]: (properties: IToggleSwitch) => {
        const isOn = properties.isOn ?? false;
        const color = isOn ? DEFAULT_TOGGLE_ON : DEFAULT_TOGGLE_OFF;
        const elements = document.querySelectorAll<HTMLElement>(
            `.${classNameForElement(StyleElement.Container, properties)}`,
        );
        /* The variable `elements` is of type `NodeListOf` which is a really weird
         * type that technically fulfils the Iterable interface, but also can't
         * actually be iterated over which really confuses unicorn and eslint.
         * Passing it through `Array.from` ensures that it is definitely an actually iterable type
         */
        /* eslint-disable-next-line unicorn/prefer-spread */
        for (const element of Array.from(elements)) {
            element.style.setProperty('--dot-color', color);
        }

        return {};
    },
    [StyleElement.Checkbox]: (properties: IToggleSwitch) => ({}),
    [StyleElement.Label]: (properties: IToggleSwitch) => ({
        backgroundColor:
            properties.backgroundColor ??
            (properties.isOn ?? false ? DEFAULT_BACKGROUND_ON : DEFAULT_BACKGROUND_OFF),
    }),
};

export function styleForElement(
    element: StyleElement,
    properties: IToggleSwitch,
    overrides: CSSProperties = {},
): CSSProperties {
    const style = { ...DEFAULTS[element], ...PROPS_APPLICATOR[element](properties), ...overrides };

    return style;
}
